import { useEffect, useState } from "react";
import { Loader2, Save, ImageIcon, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";

/**
 * Admin editor for the flagship landing page (Nexus home / route "/").
 * Edits ONLY the sections NexusHomePage.jsx actually renders:
 *   site, hero (title_prefix + rotator_words), trust, services_four,
 *   journey, projects, people, secondary, contact.
 *
 * The legacy sections (services_grid, showcase, why_elido, plus hero.title
 * and hero.bullets) are intentionally removed from the admin surface —
 * the backend still accepts them for backwards compatibility with any
 * stored doc but they are no longer surfaced or edited here.
 *
 * Every section that renders an image on the public site exposes an
 * image-URL input so the admin can paste a Cloudinary / S3 / any public
 * URL without a redeploy.
 */

const SECTION_DEFS = [
  { key: "site", label: "Sajt (logga & nav)" },
  { key: "hero", label: "Hero" },
  { key: "trust", label: "Trust — logos & pelare" },
  { key: "services_four", label: "Fyra tjänster" },
  { key: "journey", label: "Kundresan" },
  { key: "projects", label: "Utvalda projekt" },
  { key: "people", label: "Människorna bakom Elido" },
  { key: "secondary", label: "Sekundärt CTA (system/automation)" },
  { key: "contact", label: "Kontaktformulär" },
];

// ── Field helpers ───────────────────────────────────────────────────────
function FieldText({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
        {label}
      </label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-[#255E69] focus:outline-none focus:ring-2 focus:ring-[#255E69]/20"
        data-testid={testId}
      />
    </div>
  );
}

function FieldArea({ label, value, onChange, placeholder, rows = 3, testId }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
        {label}
      </label>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-[#255E69] focus:outline-none focus:ring-2 focus:ring-[#255E69]/20 resize-y"
        data-testid={testId}
      />
    </div>
  );
}

function FieldImageUrl({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide flex items-center gap-1.5">
        <ImageIcon className="w-3.5 h-3.5" />
        {label}
      </label>
      <div className="flex gap-2">
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="flex-1 px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:border-[#255E69] focus:outline-none focus:ring-2 focus:ring-[#255E69]/20"
          data-testid={testId}
        />
        {value ? (
          <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex-shrink-0">
            <img src={value} alt="preview" className="w-full h-full object-cover" />
          </div>
        ) : null}
      </div>
      <p className="text-xs text-gray-400 mt-1">
        Klistra in en publik URL (Cloudinary, S3, eller annan CDN).
      </p>
    </div>
  );
}

function SectionShell({ title, children, onSave, saving, testId }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#255E69] text-white text-sm font-medium rounded-lg hover:bg-[#1d4a54] transition-colors disabled:opacity-50"
          data-testid={`${testId}-save`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Spara
        </button>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

// Small helper that renders an array of subitems with up/down/remove and Add.
function ArrayEditor({ title, items, onMove, onRemove, onAdd, renderItem, testIdPrefix }) {
  return (
    <div className="pt-2">
      <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
        {title}
      </label>
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="rounded-lg border border-gray-200 p-3 bg-gray-50/60" data-testid={`${testIdPrefix}-row-${idx}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                #{idx + 1}
              </span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => onMove(idx, idx - 1)} disabled={idx === 0} className="p-1 text-gray-400 hover:text-[#255E69] disabled:opacity-30" aria-label="Flytta upp">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => onMove(idx, idx + 1)} disabled={idx === items.length - 1} className="p-1 text-gray-400 hover:text-[#255E69] disabled:opacity-30" aria-label="Flytta ner">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => onRemove(idx)} className="p-1 text-gray-400 hover:text-red-500" aria-label="Ta bort">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            {renderItem(item, idx)}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={onAdd}
        className="mt-3 inline-flex items-center gap-2 text-sm text-[#255E69] font-semibold hover:underline"
        data-testid={`${testIdPrefix}-add`}
      >
        <Plus className="w-4 h-4" /> Lägg till
      </button>
    </div>
  );
}

export default function NexusHomeEditor() {
  const { getAuthHeaders } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/website/nexus-home`, { credentials: "include" });
        const data = await res.json();
        if (data?.success) setContent(data.content);
      } catch (e) {
        toast.error("Kunde inte ladda startsidan");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setSectionField = (sectionKey, field, value) => {
    setContent((prev) => ({
      ...prev,
      [sectionKey]: { ...(prev?.[sectionKey] || {}), [field]: value },
    }));
  };

  const setArrayItemField = (sectionKey, listField, index, itemField, value) => {
    setContent((prev) => {
      const list = [...((prev?.[sectionKey]?.[listField]) || [])];
      list[index] = { ...list[index], [itemField]: value };
      return {
        ...prev,
        [sectionKey]: { ...(prev?.[sectionKey] || {}), [listField]: list },
      };
    });
  };

  const addArrayItem = (sectionKey, listField, defaultItem) => {
    setContent((prev) => ({
      ...prev,
      [sectionKey]: {
        ...(prev?.[sectionKey] || {}),
        [listField]: [...((prev?.[sectionKey]?.[listField]) || []), defaultItem],
      },
    }));
  };

  const removeArrayItem = (sectionKey, listField, index) => {
    setContent((prev) => {
      const list = [...((prev?.[sectionKey]?.[listField]) || [])];
      list.splice(index, 1);
      return {
        ...prev,
        [sectionKey]: { ...(prev?.[sectionKey] || {}), [listField]: list },
      };
    });
  };

  const moveArrayItem = (sectionKey, listField, fromIdx, toIdx) => {
    setContent((prev) => {
      const list = [...((prev?.[sectionKey]?.[listField]) || [])];
      if (toIdx < 0 || toIdx >= list.length) return prev;
      const [item] = list.splice(fromIdx, 1);
      list.splice(toIdx, 0, item);
      return {
        ...prev,
        [sectionKey]: { ...(prev?.[sectionKey] || {}), [listField]: list },
      };
    });
  };

  const saveSection = async (sectionKey) => {
    setSavingSection(sectionKey);
    try {
      const res = await fetch(`${API_URL}/api/website/nexus-home`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          section: sectionKey,
          content: content[sectionKey] || {},
        }),
      });
      if (!res.ok) throw new Error("save failed");
      toast.success(`${SECTION_DEFS.find((s) => s.key === sectionKey)?.label} sparad`);
    } catch (e) {
      toast.error("Kunde inte spara");
    } finally {
      setSavingSection(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!content) {
    return (
      <div className="text-center py-16 text-gray-500" data-testid="nexus-editor-empty">
        Inget innehåll kunde laddas.
      </div>
    );
  }

  const site = content.site || {};
  const hero = content.hero || {};
  const trust = content.trust || {};
  const svc4 = content.services_four || {};
  const journey = content.journey || {};
  const projects = content.projects || {};
  const people = content.people || {};
  const secondary = content.secondary || {};
  const contact = content.contact || {};

  return (
    <div className="space-y-5" data-testid="nexus-home-editor">

      {/* SITE — logo & nav */}
      <SectionShell
        title="Sajt — logga & navigering"
        onSave={() => saveSection("site")}
        saving={savingSection === "site"}
        testId="nexus-section-site"
      >
        <FieldImageUrl
          label="Logo-URL (visas i header på alla huvudsidor)"
          value={site.logo_url}
          onChange={(v) => setSectionField("site", "logo_url", v)}
          testId="nexus-site-logo-url"
        />
        <ArrayEditor
          title="Navigeringslänkar"
          items={site.nav_links || []}
          onMove={(f, t) => moveArrayItem("site", "nav_links", f, t)}
          onRemove={(i) => removeArrayItem("site", "nav_links", i)}
          onAdd={() => addArrayItem("site", "nav_links", { label: "", href: "" })}
          renderItem={(link, idx) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <FieldText label="Etikett" value={link.label} onChange={(v) => setArrayItemField("site", "nav_links", idx, "label", v)} />
              <FieldText label="Href (/path, #anchor eller https://…)" value={link.href} onChange={(v) => setArrayItemField("site", "nav_links", idx, "href", v)} />
            </div>
          )}
          testIdPrefix="nexus-site-nav"
        />
      </SectionShell>

      {/* HERO */}
      <SectionShell
        title="Hero (title_prefix + roterande ord)"
        onSave={() => saveSection("hero")}
        saving={savingSection === "hero"}
        testId="nexus-section-hero"
      >
        <FieldText
          label="Eyebrow (liten text ovanför rubriken)"
          value={hero.eyebrow}
          onChange={(v) => setSectionField("hero", "eyebrow", v)}
          testId="nexus-hero-eyebrow-input"
        />
        <FieldText
          label="Rubrikens fasta prefix (t.ex. 'Vi hjälper ditt företag med')"
          value={hero.title_prefix}
          onChange={(v) => setSectionField("hero", "title_prefix", v)}
          testId="nexus-hero-title-prefix-input"
        />
        <FieldArea
          label="Underrubrik (subheadline)"
          value={hero.subheadline}
          onChange={(v) => setSectionField("hero", "subheadline", v)}
          testId="nexus-hero-subheadline-input"
        />
        <ArrayEditor
          title="Roterande ord (rotator_words)"
          items={hero.rotator_words || []}
          onMove={(f, t) => moveArrayItem("hero", "rotator_words", f, t)}
          onRemove={(i) => removeArrayItem("hero", "rotator_words", i)}
          onAdd={() => addArrayItem("hero", "rotator_words", { label: "", href: "" })}
          renderItem={(word, idx) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <FieldText
                label="Etikett (t.ex. 'hemsidor som säljer')"
                value={word.label}
                onChange={(v) => setArrayItemField("hero", "rotator_words", idx, "label", v)}
                testId={`nexus-hero-rotator-${idx}-label`}
              />
              <FieldText
                label="Href (t.ex. /hemsidor)"
                value={word.href}
                onChange={(v) => setArrayItemField("hero", "rotator_words", idx, "href", v)}
                testId={`nexus-hero-rotator-${idx}-href`}
              />
            </div>
          )}
          testIdPrefix="nexus-hero-rotator"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldText label="Primär CTA-text" value={hero.cta_label} onChange={(v) => setSectionField("hero", "cta_label", v)} testId="nexus-hero-cta-label-input" />
          <FieldText label="Primär CTA-href" value={hero.cta_href} onChange={(v) => setSectionField("hero", "cta_href", v)} testId="nexus-hero-cta-href-input" />
          <FieldText label="Sekundär CTA-text" value={hero.secondary_cta_label} onChange={(v) => setSectionField("hero", "secondary_cta_label", v)} testId="nexus-hero-cta2-label-input" />
          <FieldText label="Sekundär CTA-href" value={hero.secondary_cta_href} onChange={(v) => setSectionField("hero", "secondary_cta_href", v)} testId="nexus-hero-cta2-href-input" />
        </div>
      </SectionShell>

      {/* TRUST */}
      <SectionShell
        title="Trust — Google-rating, kundlogos & pelare"
        onSave={() => saveSection("trust")}
        saving={savingSection === "trust"}
        testId="nexus-section-trust"
      >
        <FieldText label="Eyebrow" value={trust.eyebrow} onChange={(v) => setSectionField("trust", "eyebrow", v)} testId="nexus-trust-eyebrow-input" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldText label="Google-recensioner URL" value={trust.google_review_url} onChange={(v) => setSectionField("trust", "google_review_url", v)} />
          <FieldText label="Google-recensioner etikett" value={trust.google_review_label} onChange={(v) => setSectionField("trust", "google_review_label", v)} />
          <FieldText label="Rating (t.ex. 5,0)" value={trust.google_rating} onChange={(v) => setSectionField("trust", "google_rating", v)} />
          <FieldText label="Rating max (t.ex. 5)" value={trust.google_rating_max} onChange={(v) => setSectionField("trust", "google_rating_max", v)} />
          <FieldText label="Antal recensioner" value={trust.google_review_count} onChange={(v) => setSectionField("trust", "google_review_count", v)} />
        </div>
        <ArrayEditor
          title="Kundlogos"
          items={trust.customer_logos || []}
          onMove={(f, t) => moveArrayItem("trust", "customer_logos", f, t)}
          onRemove={(i) => removeArrayItem("trust", "customer_logos", i)}
          onAdd={() => addArrayItem("trust", "customer_logos", { name: "", logo_url: "", url: "" })}
          renderItem={(logo, idx) => (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldText label="Namn" value={logo.name} onChange={(v) => setArrayItemField("trust", "customer_logos", idx, "name", v)} />
                <FieldText label="Länk (case-sida)" value={logo.url} onChange={(v) => setArrayItemField("trust", "customer_logos", idx, "url", v)} />
              </div>
              <div className="mt-3">
                <FieldImageUrl label="Logo-bild-URL" value={logo.logo_url} onChange={(v) => setArrayItemField("trust", "customer_logos", idx, "logo_url", v)} />
              </div>
            </>
          )}
          testIdPrefix="nexus-trust-logo"
        />
        <ArrayEditor
          title="Pelare (points)"
          items={trust.points || []}
          onMove={(f, t) => moveArrayItem("trust", "points", f, t)}
          onRemove={(i) => removeArrayItem("trust", "points", i)}
          onAdd={() => addArrayItem("trust", "points", { icon: "Users", title: "", description: "" })}
          renderItem={(p, idx) => (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldText label="Ikon (Users, Layers, Compass…)" value={p.icon} onChange={(v) => setArrayItemField("trust", "points", idx, "icon", v)} />
                <FieldText label="Titel" value={p.title} onChange={(v) => setArrayItemField("trust", "points", idx, "title", v)} />
              </div>
              <div className="mt-3">
                <FieldArea label="Beskrivning" value={p.description} onChange={(v) => setArrayItemField("trust", "points", idx, "description", v)} />
              </div>
            </>
          )}
          testIdPrefix="nexus-trust-point"
        />
      </SectionShell>

      {/* SERVICES FOUR */}
      <SectionShell
        title="Fyra tjänster (services_four)"
        onSave={() => saveSection("services_four")}
        saving={savingSection === "services_four"}
        testId="nexus-section-services-four"
      >
        <FieldText label="Sektionsrubrik" value={svc4.title} onChange={(v) => setSectionField("services_four", "title", v)} testId="nexus-svc4-title-input" />
        <FieldArea label="Sektionsbeskrivning" value={svc4.subtitle} onChange={(v) => setSectionField("services_four", "subtitle", v)} />
        <ArrayEditor
          title="Kort"
          items={svc4.cards || []}
          onMove={(f, t) => moveArrayItem("services_four", "cards", f, t)}
          onRemove={(i) => removeArrayItem("services_four", "cards", i)}
          onAdd={() => addArrayItem("services_four", "cards", { key: "", title: "", description: "", icon: "Sparkles", href: "", cta_label: "" })}
          renderItem={(card, idx) => (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldText label="Nyckel (slug)" value={card.key} onChange={(v) => setArrayItemField("services_four", "cards", idx, "key", v)} />
                <FieldText label="Ikon (Globe, Megaphone, Search, Bot…)" value={card.icon} onChange={(v) => setArrayItemField("services_four", "cards", idx, "icon", v)} />
                <FieldText label="Titel" value={card.title} onChange={(v) => setArrayItemField("services_four", "cards", idx, "title", v)} />
                <FieldText label="Länk (href)" value={card.href} onChange={(v) => setArrayItemField("services_four", "cards", idx, "href", v)} />
                <FieldText label="CTA-etikett" value={card.cta_label} onChange={(v) => setArrayItemField("services_four", "cards", idx, "cta_label", v)} />
              </div>
              <div className="mt-3">
                <FieldArea label="Beskrivning" value={card.description} onChange={(v) => setArrayItemField("services_four", "cards", idx, "description", v)} />
              </div>
            </>
          )}
          testIdPrefix="nexus-svc4-card"
        />
      </SectionShell>

      {/* JOURNEY */}
      <SectionShell
        title="Kundresan (journey)"
        onSave={() => saveSection("journey")}
        saving={savingSection === "journey"}
        testId="nexus-section-journey"
      >
        <FieldText label="Eyebrow" value={journey.eyebrow} onChange={(v) => setSectionField("journey", "eyebrow", v)} />
        <FieldText label="Sektionsrubrik" value={journey.title} onChange={(v) => setSectionField("journey", "title", v)} />
        <FieldArea label="Sektionsbeskrivning" value={journey.subtitle} onChange={(v) => setSectionField("journey", "subtitle", v)} />
        <ArrayEditor
          title="Etapper"
          items={journey.stages || []}
          onMove={(f, t) => moveArrayItem("journey", "stages", f, t)}
          onRemove={(i) => removeArrayItem("journey", "stages", i)}
          onAdd={() => addArrayItem("journey", "stages", { icon: "Megaphone", title: "", description: "" })}
          renderItem={(s, idx) => (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldText label="Ikon (Megaphone, Globe, MessageSquare, Repeat…)" value={s.icon} onChange={(v) => setArrayItemField("journey", "stages", idx, "icon", v)} />
                <FieldText label="Titel" value={s.title} onChange={(v) => setArrayItemField("journey", "stages", idx, "title", v)} />
              </div>
              <div className="mt-3">
                <FieldArea label="Beskrivning" value={s.description} onChange={(v) => setArrayItemField("journey", "stages", idx, "description", v)} />
              </div>
            </>
          )}
          testIdPrefix="nexus-journey-stage"
        />
      </SectionShell>

      {/* PROJECTS */}
      <SectionShell
        title="Utvalda projekt (projects)"
        onSave={() => saveSection("projects")}
        saving={savingSection === "projects"}
        testId="nexus-section-projects"
      >
        <FieldText label="Eyebrow" value={projects.eyebrow} onChange={(v) => setSectionField("projects", "eyebrow", v)} />
        <FieldText label="Sektionsrubrik" value={projects.title} onChange={(v) => setSectionField("projects", "title", v)} />
        <FieldArea label="Sektionsbeskrivning" value={projects.subtitle} onChange={(v) => setSectionField("projects", "subtitle", v)} />
        <ArrayEditor
          title="Projekt"
          items={projects.items || []}
          onMove={(f, t) => moveArrayItem("projects", "items", f, t)}
          onRemove={(i) => removeArrayItem("projects", "items", i)}
          onAdd={() => addArrayItem("projects", "items", { name: "", category: "", description: "", image_url: "", url: "" })}
          renderItem={(item, idx) => (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldText label="Namn" value={item.name} onChange={(v) => setArrayItemField("projects", "items", idx, "name", v)} />
                <FieldText label="Kategori" value={item.category} onChange={(v) => setArrayItemField("projects", "items", idx, "category", v)} />
                <FieldText label="Länk (URL)" value={item.url} onChange={(v) => setArrayItemField("projects", "items", idx, "url", v)} />
              </div>
              <div className="mt-3">
                <FieldArea label="Beskrivning" value={item.description} onChange={(v) => setArrayItemField("projects", "items", idx, "description", v)} />
              </div>
              <div className="mt-3">
                <FieldImageUrl label="Bild-URL" value={item.image_url} onChange={(v) => setArrayItemField("projects", "items", idx, "image_url", v)} />
              </div>
            </>
          )}
          testIdPrefix="nexus-projects-item"
        />
      </SectionShell>

      {/* PEOPLE */}
      <SectionShell
        title="Människorna bakom Elido (people)"
        onSave={() => saveSection("people")}
        saving={savingSection === "people"}
        testId="nexus-section-people"
      >
        <FieldText label="Eyebrow" value={people.eyebrow} onChange={(v) => setSectionField("people", "eyebrow", v)} />
        <FieldText label="Sektionsrubrik" value={people.title} onChange={(v) => setSectionField("people", "title", v)} />
        <FieldArea label="Sektionsbeskrivning" value={people.description} onChange={(v) => setSectionField("people", "description", v)} />
        <ArrayEditor
          title="Personer"
          items={people.members || []}
          onMove={(f, t) => moveArrayItem("people", "members", f, t)}
          onRemove={(i) => removeArrayItem("people", "members", i)}
          onAdd={() => addArrayItem("people", "members", { name: "", role: "Elido AB", image_url: "", phone: "", email: "" })}
          renderItem={(m, idx) => (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldText label="Namn" value={m.name} onChange={(v) => setArrayItemField("people", "members", idx, "name", v)} />
                <FieldText label="Roll" value={m.role} onChange={(v) => setArrayItemField("people", "members", idx, "role", v)} />
                <FieldText label="Telefon" value={m.phone} onChange={(v) => setArrayItemField("people", "members", idx, "phone", v)} />
                <FieldText label="E-post" value={m.email} onChange={(v) => setArrayItemField("people", "members", idx, "email", v)} />
              </div>
              <div className="mt-3">
                <FieldImageUrl label="Porträtt-URL" value={m.image_url} onChange={(v) => setArrayItemField("people", "members", idx, "image_url", v)} />
              </div>
            </>
          )}
          testIdPrefix="nexus-people-member"
        />
      </SectionShell>

      {/* SECONDARY */}
      <SectionShell
        title="Sekundärt CTA — system, integrationer, automation (secondary)"
        onSave={() => saveSection("secondary")}
        saving={savingSection === "secondary"}
        testId="nexus-section-secondary"
      >
        <FieldText label="Eyebrow" value={secondary.eyebrow} onChange={(v) => setSectionField("secondary", "eyebrow", v)} />
        <FieldText label="Rubrik" value={secondary.title} onChange={(v) => setSectionField("secondary", "title", v)} />
        <FieldArea label="Beskrivning" value={secondary.description} onChange={(v) => setSectionField("secondary", "description", v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldText label="CTA-text" value={secondary.cta_label} onChange={(v) => setSectionField("secondary", "cta_label", v)} />
          <FieldText label="CTA-href" value={secondary.cta_href} onChange={(v) => setSectionField("secondary", "cta_href", v)} />
        </div>
      </SectionShell>

      {/* CONTACT */}
      <SectionShell
        title="Kontaktformulär (contact)"
        onSave={() => saveSection("contact")}
        saving={savingSection === "contact"}
        testId="nexus-section-contact"
      >
        <FieldText label="Rubrik" value={contact.title} onChange={(v) => setSectionField("contact", "title", v)} />
        <FieldArea label="Underrubrik" value={contact.subtitle} onChange={(v) => setSectionField("contact", "subtitle", v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldText label="Submit-knapp-text" value={contact.submit_label} onChange={(v) => setSectionField("contact", "submit_label", v)} />
          <FieldText label="Tack-meddelande" value={contact.success_message} onChange={(v) => setSectionField("contact", "success_message", v)} />
        </div>
      </SectionShell>

    </div>
  );
}
